import React from "react";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";

// --- Types ---
export interface StepData {
  icon?: string | JSX.Element;
  title: string;
  steplabel?: string;
  displayText?: string;
}
interface EligibilityCardProps {
  steps: StepData[];
  showStepLabel?: boolean;
}

const EligibilityCard: React.FC<EligibilityCardProps> = ({
  steps,
  showStepLabel,
}) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));

  // --- Internal component to render each step box ---
  const StepItem: React.FC<StepData & { stepIndex: number }> = ({
    icon,
    title,
    stepIndex,
  }) => (
    <Box
      sx={{
        width: "100%",
        // maxWidth: { xs: "100%", sm: 182 },
        // minWidth: { xs: "100%", sm: 160 },
        // height: { xs: "auto", sm: "150px" },
        borderRadius: isXsOrSm ? "35px" : "24px",
        p: { xs: 2, sm: 2 },
        my: { xs: 1, sm: 0 },
        borderWidth: "2px",
        borderStyle: "dotted",
        borderColor: "#00B3B5",
        background: "#00B3B514",
        display: "flex",
        flexDirection: isXsOrSm ? "row" : "column",
        alignItems: isXsOrSm ? "center" : "flex-start",
        textAlign: "left",
        justifyContent: "flex-start",
        gap: 1.5,
        boxSizing: "border-box",
        overflowWrap: "break-word",
        wordBreak: "break-word",
        flexShrink: 0,
      }}
    >
      {/* Icon */}
      {icon && (
        <Box
          sx={{
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {typeof icon === "string" ? (
            <img
              src={icon}
              alt={`${title} icon`}
              width={40}
              height={40}
              loading="lazy"
              style={{ objectFit: "contain" }}
            />
          ) : (
            icon
          )}
        </Box>
      )}

      {/* Content Container */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Step Label with Index */}
        {!isXsOrSm && showStepLabel && (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              fontSize: "13px",
              color: "#868a8aff",
              mb: 0.5,
              whiteSpace: "normal",
              lineHeight: "1.2em",
            }}
          >
            Step {stepIndex + 1}
          </Typography>
        )}

        {/* Title */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: isXsOrSm ? "15px" : "13px",
            color: isXsOrSm ? "#5a5959ff" : "#000",
            lineHeight: "1.4em",
            wordBreak: "break-word",
          }}
        >
          {title}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
      }}
      className="eligibility-card"
    >
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        alignItems="stretch"
        sx={{
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: { xs: "nowrap", sm: "nowrap" },
          justifyContent: "space-between",
        }}
      >
        {steps?.map((step, index) => (
          <Grid
            item
            key={index}
            sx={{
              width: { xs: "100%", sm: "auto" },
              flex: { xs: "0 0 auto", sm: "1 1 auto" },
              // maxWidth: { xs: "100%", sm: 200 },
              display: "flex",
            }}
          >
            <StepItem
              icon={step?.icon}
              title={step?.title ?? ""}
              stepIndex={index}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default EligibilityCard;
